import React from 'react';
import { ArrowRightIcon, CheckCircle2Icon, ChevronRightIcon, CircleCheckIcon, DumbbellIcon, LockKeyholeIcon, ShieldCheckIcon, SparklesIcon, TargetIcon, TrophyIcon } from 'lucide-react';
import { FadeIn } from './components/FadeIn';
import { LiveVisitors } from './components/LiveVisitors';
import { UrgencyBar } from './components/UrgencyBar';
const CHECKOUT_URL = 'https://boxeo-ya.impultienda.ar/checkout';
const modules = [{
  number: '01',
  title: 'Fundamentos',
  description: 'Guardia, postura, distancia y desplazamientos para construir una base firme.'
}, {
  number: '02',
  title: 'Golpes',
  description: 'Rectos, ganchos, uppercuts y combinaciones con intención y precisión.'
}, {
  number: '03',
  title: 'Defensa',
  description: 'Bloqueos, esquivas y contraataques para moverte con más confianza.'
}, {
  number: '04',
  title: 'Físico',
  description: 'Resistencia, potencia y movilidad aplicadas a cada round.'
}, {
  number: '05',
  title: 'Táctica',
  description: 'Lectura del rival, control de ritmo y decisiones dentro del ring.'
}, {
  number: '06',
  title: 'Mentalidad',
  description: 'Disciplina, foco y un plan sostenible para no abandonar.'
}];
const bonuses = ['Combos de Campeón — guía de combinaciones', 'Reto Boxeador — plan de 30 días', 'Detector de Errores del Boxeador', 'Dominio del Saco de Boxeo', 'Diario del Boxeador Disciplinado'];
function CheckoutButton({
  compact = false
}: {
  compact?: boolean;
}) {
  return <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className={`group inline-flex items-center justify-center gap-2 rounded-xl bg-boxing-red px-6 font-display font-black uppercase text-white shadow-[0_0_28px_rgba(217,25,32,0.28)] transition duration-200 hover:bg-boxing-redHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-boxing-dark ${compact ? 'py-3 text-base' : 'w-full py-4 text-lg sm:w-auto sm:px-9 sm:text-xl'}`} data-id="element-0">
      {compact ? 'Quiero empezar ahora' : 'Ir al pago seguro'}
      <ChevronRightIcon className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1" />
    </a>;
}
function OffersButton() {
  return <a href="#pricing" className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-boxing-gold bg-boxing-surface px-6 py-4 font-display text-lg font-black uppercase text-boxing-gold transition duration-200 hover:bg-boxing-surfaceHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-boxing-gold focus-visible:ring-offset-2 focus-visible:ring-offset-boxing-dark sm:w-auto sm:px-9 sm:text-xl" data-id="element-2">
      Ver ofertas del pack
      <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-3" />
    </a>;
}
export function App() {
  return <div className="min-h-screen bg-boxing-dark font-sans text-boxing-ink" data-id="element-4">
      <UrgencyBar data-id="element-5" />

      <main className="overflow-hidden pt-14" data-id="element-6">
        <section className="relative isolate flex min-h-[calc(100svh-3.5rem)] items-center px-4 py-16 sm:py-20" data-id="element-7">
          <div className="pointer-events-none absolute inset-0 -z-10 opacity-30 [background-image:linear-gradient(rgba(217,25,32,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(217,25,32,0.1)_1px,transparent_1px)] [background-size:52px_52px]" data-id="element-8" />
          <div className="pointer-events-none absolute left-1/2 top-16 -z-10 h-72 w-72 -translate-x-1/2 rounded-full bg-boxing-red/10 blur-3xl" data-id="element-9" />

          <div className="mx-auto max-w-5xl text-center" data-id="element-10">
            <FadeIn data-id="element-11">
              <span className="inline-flex items-center gap-2 rounded-full border border-boxing-muted bg-boxing-surface px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-gray-300 sm:text-xs" data-id="element-12">
                <SparklesIcon className="h-3.5 w-3.5 text-boxing-gold" aria-hidden="true" data-id="element-13" />
                Formación deportiva digital
              </span>
            </FadeIn>

            <FadeIn delay={0.08} data-id="element-14">
              <h1 className="mx-auto mt-5 max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-boxing-red sm:text-6xl lg:text-7xl" data-id="element-15">
                Resolver todas tus clases
                <br data-id="element-16" />
                <span className="text-white" data-id="element-17">de boxeo con este pack.</span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.14} data-id="element-18">
              <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-boxing-gold sm:text-lg" data-id="element-19">
                El método completo — 6 tomos + 5 bonos
              </p>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-gray-300 sm:text-lg" data-id="element-20">
                Un camino claro para entrenar con técnica, entender cada etapa y
                progresar con una base que podés sostener.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-21">
              <div className="relative mx-auto mt-7 max-w-3xl" data-id="element-22">
                <div className="pointer-events-none absolute inset-x-20 bottom-5 h-20 rounded-full bg-boxing-red/15 blur-3xl" data-id="element-23" />
                <img src="https://cdn.magicpatterns.com/uploads/9xmQbA5kXV9jchJAk22UuX/imagen_4.png" alt="Mockup del pack BOXEO 360 con sus seis tomos" className="relative mx-auto w-full rounded-2xl border border-boxing-muted/70 shadow-[0_18px_45px_rgba(0,0,0,0.45)]" data-id="element-24" />
              </div>
            </FadeIn>

            <FadeIn delay={0.28} data-id="element-25">
              <div className="mt-8 flex flex-col items-center gap-4" data-id="element-26">
                <LiveVisitors data-id="element-27" />
                <div className="flex w-full max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:justify-center" data-id="element-28">
                  <OffersButton data-id="element-29" />
                  <CheckoutButton data-id="element-30" />
                </div>
                <p className="text-sm font-semibold text-gray-400" data-id="element-31">
                  Mirá la oferta primero o avanzá directo a pago seguro.
                </p>
              </div>
            </FadeIn>
          </div>

          <aside className="fixed bottom-5 left-5 z-30 hidden w-64 border border-boxing-muted bg-boxing-surface p-4 shadow-[0_10px_28px_rgba(0,0,0,0.35)] xl:block" aria-label="Novedades del pack" data-id="element-32">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-boxing-gold" data-id="element-33">
              Novedades del pack
            </p>
            <div className="mt-3 space-y-3 border-l border-boxing-red/60 pl-3" data-id="element-34">
              <p className="text-xs leading-relaxed text-gray-300" data-id="element-35">
                <span className="font-bold text-white" data-id="element-36">Pack inicial:</span> 6
                tomos para estudiar desde los fundamentos.
              </p>
              <p className="text-xs leading-relaxed text-gray-300" data-id="element-37">
                <span className="font-bold text-white" data-id="element-38">Acceso digital:</span>{' '}
                disponible al confirmar tu compra.
              </p>
            </div>
          </aside>
        </section>

        <section className="border-y border-boxing-muted bg-boxing-surface px-4 py-14 sm:py-20" data-id="element-39">
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center" data-id="element-40">
            <FadeIn data-id="element-41">
              <p className="text-sm font-black uppercase tracking-[0.14em] text-boxing-gold" data-id="element-42">
                ¿Te suena familiar?
              </p>
              <h2 className="mt-3 font-display text-3xl font-black uppercase leading-tight text-white sm:text-5xl" data-id="element-43">
                No necesitás más ganas.
                <br data-id="element-44" />
                <span className="text-boxing-red" data-id="element-45">Necesitás un sistema.</span>
              </h2>
            </FadeIn>
            <div className="grid gap-3 sm:grid-cols-3" data-id="element-46">
              {[['Entrenás sin orden', 'Cada sesión se siente distinta y no sabés qué mejorar primero.'], ['Golpeás sin técnica', 'Ponés energía, pero te falta una estructura clara para ejecutar.'], ['Perdés constancia', 'Sin un plan, es fácil dejarlo cuando llegan las dudas.']].map(([title, description], index) => <FadeIn key={title} delay={index * 0.08} data-id="element-47">
                  <article className="h-full rounded-xl border border-boxing-muted bg-boxing-dark p-5" data-id="element-48">
                    <span className="font-display text-3xl text-boxing-red" data-id="element-49">
                      0{index + 1}
                    </span>
                    <h3 className="mt-7 text-lg font-black text-white" data-id="element-50">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-51">
                      {description}
                    </p>
                  </article>
                </FadeIn>)}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:py-24" data-id="element-52">
          <div className="mx-auto max-w-6xl" data-id="element-53">
            <FadeIn className="mx-auto max-w-2xl text-center" data-id="element-54">
              <p className="text-sm font-black uppercase tracking-[0.14em] text-boxing-gold" data-id="element-55">
                Un recorrido completo
              </p>
              <h2 className="mt-3 font-display text-3xl font-black uppercase text-white sm:text-5xl" data-id="element-56">
                Seis tomos.{' '}
                <span className="text-boxing-red" data-id="element-57">Una dirección.</span>
              </h2>
              <p className="mt-4 leading-relaxed text-gray-400" data-id="element-58">
                No es una colección de tips aislados. Cada tomo te acompaña
                desde los fundamentos hasta una preparación más consciente.
              </p>
            </FadeIn>

            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-59">
              {modules.map((module, index) => <FadeIn key={module.number} delay={index * 0.05} data-id="element-60">
                  <article className="group h-full rounded-2xl border border-boxing-muted bg-boxing-surface p-6 transition-colors hover:border-boxing-red/60" data-id="element-61">
                    <div className="flex items-start justify-between" data-id="element-62">
                      <span className="font-display text-4xl text-boxing-red" data-id="element-63">
                        {module.number}
                      </span>
                      <TargetIcon className="h-5 w-5 text-boxing-gold" aria-hidden="true" data-id="element-64" />
                    </div>
                    <h3 className="mt-10 text-xl font-black text-white" data-id="element-65">
                      {module.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-66">
                      {module.description}
                    </p>
                  </article>
                </FadeIn>)}
            </div>
          </div>
        </section>

        <section className="bg-boxing-surface px-4 py-16 sm:py-24" data-id="element-67">
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2 lg:items-center" data-id="element-68">
            <FadeIn data-id="element-69">
              <div className="rounded-2xl border border-boxing-red/50 bg-boxing-dark p-7 shadow-[0_0_40px_rgba(217,25,32,0.1)] sm:p-10" data-id="element-70">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-boxing-red text-white" data-id="element-71">
                  <TrophyIcon className="h-6 w-6" aria-hidden="true" data-id="element-72" />
                </div>
                <p className="mt-7 text-sm font-black uppercase tracking-[0.14em] text-boxing-gold" data-id="element-73">
                  Además, recibís 5 bonos
                </p>
                <h2 className="mt-3 font-display text-3xl font-black uppercase text-white sm:text-4xl" data-id="element-74">
                  Más herramientas para practicar.
                </h2>
                <p className="mt-4 leading-relaxed text-gray-400" data-id="element-75">
                  Material complementario para organizar tu trabajo, revisar
                  detalles y mantener tu entrenamiento en marcha.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-76">
              <ul className="space-y-3" data-id="element-77">
                {bonuses.map(bonus => <li key={bonus} className="flex gap-3 rounded-xl border border-boxing-muted bg-boxing-dark p-4" data-id="element-78">
                    <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-boxing-red" aria-hidden="true" data-id="element-79" />
                    <span className="font-semibold text-gray-200" data-id="element-80">{bonus}</span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </section>

        <section className="px-4 py-16 sm:py-24" data-id="element-81">
          <div className="mx-auto max-w-4xl text-center" data-id="element-82">
            <FadeIn data-id="element-83">
              <DumbbellIcon className="mx-auto h-9 w-9 text-boxing-gold" aria-hidden="true" data-id="element-84" />
              <h2 className="mt-4 font-display text-3xl font-black uppercase text-white sm:text-5xl" data-id="element-85">
                Está pensado para vos si...
              </h2>
            </FadeIn>
            <div className="mt-9 grid gap-3 text-left sm:grid-cols-2" data-id="element-86">
              {['Querés comenzar a entrenar con una base ordenada.', 'Buscás comprender el porqué detrás de cada movimiento.', 'Querés una guía para practicar a tu propio ritmo.', 'Estás listo para construir constancia, no buscar atajos.'].map((item, index) => <FadeIn key={item} delay={index * 0.06} data-id="element-87">
                  <div className="flex h-full items-start gap-3 rounded-xl border border-boxing-muted bg-boxing-surface p-5" data-id="element-88">
                    <CircleCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-boxing-gold" aria-hidden="true" data-id="element-89" />
                    <p className="font-semibold text-gray-200" data-id="element-90">{item}</p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-16 bg-boxing-surface px-4 py-16 sm:py-24" data-id="element-91">
          <div className="mx-auto max-w-xl" data-id="element-92">
            <FadeIn className="text-center" data-id="element-93">
              <LiveVisitors data-id="element-94" />
              <p className="mt-6 text-sm font-black uppercase tracking-[0.14em] text-boxing-gold" data-id="element-95">
                Acceso completo
              </p>
              <h2 className="mt-3 font-display text-3xl font-black uppercase text-white sm:text-5xl" data-id="element-96">
                Tu pack BOXEO 360
              </h2>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-97">
              <article className="relative mt-8 overflow-hidden rounded-3xl border-2 border-boxing-red bg-boxing-dark p-6 shadow-[0_0_55px_rgba(217,25,32,0.16)] sm:p-10" data-id="element-98">
                <div className="absolute right-0 top-0 rounded-bl-xl bg-boxing-red px-4 py-2 text-xs font-black uppercase tracking-wide text-white" data-id="element-99">
                  Lanzamiento
                </div>
                <h3 className="font-display text-2xl font-black uppercase text-white" data-id="element-100">
                  Método Integral
                </h3>
                <p className="mt-2 leading-relaxed text-gray-400" data-id="element-101">
                  Los 6 tomos de BOXEO 360, los 5 bonos y acceso digital
                  inmediato.
                </p>

                <ul className="mt-7 space-y-3" data-id="element-102">
                  {['6 tomos de formación progresiva', '5 bonos prácticos complementarios', 'Acceso inmediato y descarga permanente'].map(item => <li key={item} className="flex items-center gap-3 text-sm font-semibold text-gray-200" data-id="element-103">
                      <CheckCircle2Icon className="h-5 w-5 shrink-0 text-boxing-red" aria-hidden="true" data-id="element-104" />
                      {item}
                    </li>)}
                </ul>

                <div className="my-8 border-y border-boxing-muted py-6 text-center" data-id="element-105">
                  <p className="text-sm font-semibold text-gray-400" data-id="element-106">
                    Precio de lanzamiento
                  </p>
                  <p className="mt-1 font-display text-5xl font-black text-boxing-red sm:text-6xl" data-id="element-107">
                    $18.990
                  </p>
                  <p className="mt-2 text-sm text-gray-500" data-id="element-108">
                    Un único pago · Acceso inmediato
                  </p>
                </div>

                <CheckoutButton data-id="element-109" />

                <div className="mt-6 grid grid-cols-2 gap-2 rounded-xl border border-green-500/20 bg-green-500/5 p-3 text-left text-xs font-bold text-green-400 sm:grid-cols-4" data-id="element-110">
                  <span className="flex items-center gap-1.5" data-id="element-111">
                    <LockKeyholeIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-112" />
                    Pago seguro con Mercado Pago
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-113">
                    <ArrowRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-114" />
                    Acceso vía mail
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-115">
                    <CheckCircle2Icon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-116" />
                    PDF inmediato
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-117">
                    <ShieldCheckIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-118" />
                    Garantía de 7 días
                  </span>
                </div>
              </article>
            </FadeIn>
          </div>
        </section>

        <section className="px-4 py-16 text-center sm:py-20" data-id="element-119">
          <FadeIn data-id="element-120">
            <ShieldCheckIcon className="mx-auto h-10 w-10 text-boxing-gold" aria-hidden="true" data-id="element-121" />
            <h2 className="mt-4 font-display text-3xl font-black uppercase text-white sm:text-5xl" data-id="element-122">
              Tu próximo round empieza hoy.
            </h2>
            <p className="mx-auto mt-4 max-w-xl leading-relaxed text-gray-400" data-id="element-123">
              Elegí entrenar con una estructura que te ayude a avanzar paso a
              paso, sin improvisar tu progreso.
            </p>
            <div className="mt-7" data-id="element-124">
              <CheckoutButton compact data-id="element-125" />
            </div>
          </FadeIn>
        </section>
      </main>

      <footer className="border-t border-boxing-muted bg-black px-4 py-8 pb-24 text-center sm:pb-8" data-id="element-126">
        <p className="font-display text-lg font-black tracking-wide text-white" data-id="element-127">
          BOXEO 360
        </p>
        <p className="mt-2 text-sm text-gray-500" data-id="element-128">
          Formación digital para quienes quieren entrenar con dirección.
        </p>
      </footer>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-boxing-muted bg-boxing-surface/95 p-3 backdrop-blur sm:hidden" data-id="element-129">
        <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-lg bg-boxing-red py-3 font-display font-black uppercase text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-130">
          Quiero el pack ahora
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-131" />
        </a>
      </div>
    </div>;
}